import { makeAutoObservable, runInAction } from 'mobx';
import { BookingListItem, BookingOption, ClientBookingModel } from "../../model/client/client-booking-model";
import { ClientServiceModel } from "../../model/client/client-service-model";
import { ClientAvailabilityProfessionalModel } from "../../model/client/client-availability-professional-model";
import { Service } from "../../model/professional/service-model";
import { Option } from "../../model/professional/option-model";
import { OptionCategory } from "../../model/professional/option-category-model";
import { HTTP_STATUS_CODE } from "../../utils/UHttp";
import toastStore from "../../store/toast-store";
import { formatDateInTimeZone } from "../../utils/timezone";

interface CartItem {
  service: {
    id: string;
    name: string;
    price: number;
    duration: number;
  };
  options: BookingOption[];
  price: number;
  duration: number;
  isOriginal: boolean;
}

class ClientBookingManageViewModel {
  // États de base
  loading: boolean = false;
  cancelling: boolean = false;
  updating: boolean = false;
  error: string | null = null;
  booking: BookingListItem | null = null;

  // Services disponibles
  services: Service[] = [];
  loadingServices: boolean = false;

  // Panier modifiable
  cartItems: CartItem[] = [];
  originalCartItems: CartItem[] = [];

  // Service en cours de configuration
  selectedService: Service | null = null;
  selectedOptions: Option[] = [];
  optionCategories: OptionCategory[] = [];
  loadingOptions: boolean = false;

  // Sélection de créneau
  selectedDate: Date | null = null;
  selectedSlot: any = null;
  availableDates: string[] = [];
  availableSlots: any[] = [];
  loadingSlots: boolean = false;

  // Mode édition
  isEditMode: boolean = false;

  protected bookingModel: ClientBookingModel;
  protected serviceModel: ClientServiceModel;
  protected availabilityModel: ClientAvailabilityProfessionalModel;

  constructor() {
    makeAutoObservable(this);
    this.bookingModel = ClientBookingModel.getInstance();
    this.serviceModel = ClientServiceModel.getInstance();
    this.availabilityModel = ClientAvailabilityProfessionalModel.getInstance();
  }

  // Getters
  get isCancelled(): boolean {
    return this.booking?.status === 'cancelled';
  }

  get isCompleted(): boolean {
    return this.booking?.status === 'completed';
  }

  get isPastOrOngoing(): boolean {
    if (!this.booking?.startTime) return false;
    const now = new Date();
    const startTime = new Date(this.booking.startTime);
    return startTime <= now;
  }

  get canEdit(): boolean {
    return !this.isCancelled && !this.isCompleted && !this.isPastOrOngoing;
  }

  get canCancel(): boolean {
    return !this.isCancelled && !this.isCompleted && !this.isPastOrOngoing;
  }

  get getBooking() {
    return this.booking;
  }

  get getLoading() {
    return this.loading;
  }

  get getError() {
    return this.error;
  }

  get getCancelling() {
    return this.cancelling;
  }

  get getUpdating() {
    return this.updating;
  }

  // Calculs du panier
  get totalDuration(): number {
    return this.cartItems.reduce((total, item) => total + item.duration, 0);
  }

  get totalPrice(): number {
    return Math.round(this.cartItems.reduce((total, item) => total + item.price, 0) * 100) / 100;
  }

  get originalTotalPrice(): number {
    return Math.round(this.originalCartItems.reduce((total, item) => total + item.price, 0) * 100) / 100;
  }

  get originalTotalDuration(): number {
    return this.originalCartItems.reduce((total, item) => total + item.duration, 0);
  }

  get priceDifference(): number {
    return Math.round((this.totalPrice - this.originalTotalPrice) * 100) / 100;
  }

  get hasDurationChanged(): boolean {
    return this.totalDuration !== this.originalTotalDuration;
  }

  get durationDifference(): number {
    return this.totalDuration - this.originalTotalDuration;
  }

  get hasChanges(): boolean {
    if (this.cartItems.length !== this.originalCartItems.length) return true;

    const currentIds = this.cartItems.map(item => item.service.id).sort().join(',');
    const originalIds = this.originalCartItems.map(item => item.service.id).sort().join(',');
    if (currentIds !== originalIds) return true;

    // Si un nouveau créneau est sélectionné
    if (this.selectedSlot) return true;

    return false;
  }

  get canSaveChanges(): boolean {
    // Si la durée a changé, un nouveau créneau doit être sélectionné
    if (this.hasDurationChanged && !this.selectedSlot) {
      return false;
    }
    return this.hasChanges && this.cartItems.length > 0 && !this.updating;
  }

  get needsTimeSlotSelection(): boolean {
    // Retourne true si la durée a changé et qu'aucun nouveau créneau n'est sélectionné
    return this.hasDurationChanged && !this.selectedSlot;
  }

  // Setters
  setLoading(value: boolean) {
    this.loading = value;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setCancelling(value: boolean) {
    this.cancelling = value;
  }

  setUpdating(value: boolean) {
    this.updating = value;
  }

  setIsEditMode(value: boolean) {
    if (value && !this.canEdit) {
      toastStore.error('Cette réservation ne peut pas être modifiée');
      return;
    }
    this.isEditMode = value;
    if (value && this.booking?.businessName) {
      this.fetchServices();
    }
  }

  setNoBookingId(): void {
    this.loading = false;
    this.setError('Aucun identifiant de réservation n\'a été fourni');
    this.booking = null;
  }

  // Charger la réservation
  async fetchBooking(id: string): Promise<void> {
    try {
      this.setLoading(true);
      this.setError(null);

      const response = await this.bookingModel.getBookingById(id);

      if (response.status === HTTP_STATUS_CODE.OK && response.data) {
        runInAction(() => {
          this.booking = response.data as BookingListItem;
          this.initializeCartFromBooking();
        });
      }
    } catch (err) {
      this.setError('Une erreur est survenue lors du chargement de la réservation');
    } finally {
      this.setLoading(false);
    }
  }

  // Initialiser le panier depuis la réservation existante
  private initializeCartFromBooking(): void {
    if (!this.booking) return;

    const cartItems: CartItem[] = [];

    if (this.booking.services && this.booking.services.length > 0) {
      // Multi-services
      this.booking.services.forEach(service => {
        cartItems.push({
          service: {
            id: service.serviceId,
            name: service.serviceName,
            price: service.price,
            duration: service.duration,
          },
          options: service.options || [],
          price: service.price + (service.options?.reduce((sum, opt) => sum + opt.price, 0) || 0),
          duration: service.duration,
          isOriginal: true,
        });
      });
    } else if (this.booking.serviceId) {
      // Legacy single service
      const serviceDuration = this.booking.serviceDuration || this.booking.totalDuration || 60;
      const servicePrice = this.booking.servicePrice || this.booking.totalPrice || 0;

      cartItems.push({
        service: {
          id: this.booking.serviceId,
          name: this.booking.serviceName || 'Service',
          price: servicePrice,
          duration: serviceDuration,
        },
        options: this.booking.options || [],
        price: servicePrice + (this.booking.options?.reduce((sum, opt) => sum + opt.price, 0) || 0),
        duration: serviceDuration,
        isOriginal: true,
      });
    }

    this.cartItems = cartItems;
    this.originalCartItems = JSON.parse(JSON.stringify(cartItems));

    // Initialiser la date sélectionnée
    if (this.booking.startTime) {
      this.selectedDate = new Date(this.booking.startTime);
    }
  }

  // Charger les services du professionnel
  async fetchServices(): Promise<void> {
    if (!this.booking?.businessName) return;

    try {
      this.loadingServices = true;

      // Utiliser businessName pour récupérer les services
      const response = await this.serviceModel.getServicesByProfessional(this.booking.businessName);

      if (response.success && response.data) {
        runInAction(() => {
          this.services = response.data;
        });
      }
    } catch (err) {
      console.error('Erreur lors du chargement des services:', err);
    } finally {
      runInAction(() => {
        this.loadingServices = false;
      });
    }
  }

  // Charger les options d'un service
  async loadOptionCategories(serviceId: string): Promise<void> {
    try {
      this.loadingOptions = true;
      const response: any = await this.serviceModel.getOptionCategoriesByService(serviceId);

      runInAction(() => {
        // La réponse peut être un tableau directement ou un objet avec une propriété data
        this.optionCategories = Array.isArray(response) ? response : (response.data || []);
      });
    } catch (err) {
      console.error('Erreur lors du chargement des options:', err);
      runInAction(() => {
        this.optionCategories = [];
      });
    } finally {
      runInAction(() => {
        this.loadingOptions = false;
      });
    }
  }

  // Sélectionner un service pour l'ajouter
  async selectServiceToAdd(service: Service): Promise<void> {
    this.selectedService = service;
    this.selectedOptions = [];
    await this.loadOptionCategories(service.id);
  }

  // Annuler la sélection de service
  cancelServiceSelection(): void {
    this.selectedService = null;
    this.selectedOptions = [];
    this.optionCategories = [];
  }

  // Sélectionner/désélectionner une option
  selectOption(option: Option): void {
    const category = this.optionCategories.find(cat => cat.id === option.categoryId);
    if (!category) return;

    if (category.isMultipleChoice) {
      const index = this.selectedOptions.findIndex(opt => opt.id === option.id);
      if (index >= 0) {
        this.selectedOptions.splice(index, 1);
      } else {
        this.selectedOptions.push(option);
      }
    } else {
      this.selectedOptions = this.selectedOptions.filter(opt => opt.categoryId !== option.categoryId);
      this.selectedOptions.push(option);
    }
  }

  // Calculer le prix d'un service avec ses options
  private calculateServicePrice(service: Service, options: Option[]): number {
    const servicePrice = typeof service.price === 'number' ?
      service.price : parseFloat(service.price as any) || 0;

    const optionsPrice = options.reduce((total, option) => {
      const optionPrice = typeof option.price === 'number' ?
        option.price : parseFloat(option.price as any) || 0;
      return total + optionPrice;
    }, 0);

    return Math.round((servicePrice + optionsPrice) * 100) / 100;
  }

  // Calculer la durée d'un service avec ses options
  private calculateServiceDuration(service: Service, options: Option[]): number {
    const serviceDuration = service.duration || 0;
    const optionsDuration = options.reduce((total, option) => {
      return total + (option.durationMinutes || 0);
    }, 0);
    return serviceDuration + optionsDuration;
  }

  // Ajouter le service sélectionné au panier
  addServiceToCart(): boolean {
    if (!this.selectedService) {
      toastStore.error('Veuillez sélectionner un service');
      return false;
    }

    const requiredCategories = this.optionCategories.filter(cat => cat.isRequired);
    const missingRequired = requiredCategories.some(cat =>
      !this.selectedOptions.some(opt => opt.categoryId === cat.id)
    );

    if (missingRequired) {
      toastStore.error('Veuillez sélectionner toutes les options obligatoires');
      return false;
    }

    const price = this.calculateServicePrice(this.selectedService, this.selectedOptions);
    const duration = this.calculateServiceDuration(this.selectedService, this.selectedOptions);

    // Convertir les options en BookingOption
    const bookingOptions: BookingOption[] = this.selectedOptions.map(opt => ({
      id: opt.id,
      name: opt.name,
      price: opt.price,
      categoryId: opt.categoryId,
    }));

    this.cartItems.push({
      service: {
        id: this.selectedService.id,
        name: this.selectedService.name,
        price: this.selectedService.price,
        duration: this.selectedService.duration || 0,
      },
      options: bookingOptions,
      price,
      duration,
      isOriginal: false,
    });

    this.cancelServiceSelection();
    toastStore.success('Service ajouté');
    return true;
  }

  // Supprimer un service du panier
  removeServiceFromCart(index: number): void {
    if (index >= 0 && index < this.cartItems.length) {
      if (this.cartItems.length === 1) {
        toastStore.error('Vous devez avoir au moins un service');
        return;
      }
      this.cartItems.splice(index, 1);
    }
  }

  // Annuler la réservation
  async cancelBooking(id: string | undefined): Promise<void> {
    try {
      this.setCancelling(true);
      if (!id) return;

      const response = await this.bookingModel.cancelBooking(id);

      if (response.status === HTTP_STATUS_CODE.OK) {
        if (this.booking) {
          this.booking.status = 'cancelled';
        }
        toastStore.success(response.message || 'Réservation annulée');
      }
    } catch (err) {
      this.setError('Une erreur est survenue lors de l\'annulation');
    } finally {
      this.setCancelling(false);
    }
  }

  // Sauvegarder les modifications
  async saveChanges(): Promise<boolean> {
    if (!this.booking || !this.canSaveChanges) return false;

    try {
      this.setUpdating(true);

      // Construire les dates complètes si un nouveau créneau est sélectionné
      let startTime: string | undefined;
      let endTime: string | undefined;

      if (this.selectedSlot && this.selectedDate) {
        // Extraire la date en heure locale sans conversion UTC
        const year = this.selectedDate.getFullYear();
        const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(this.selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Combiner avec les heures du slot
        const localStartDateTime = `${dateStr}T${this.selectedSlot.startTime}:00`;
        const localEndDateTime = `${dateStr}T${this.selectedSlot.endTime}:00`;

        const startDate = new Date(localStartDateTime);
        const endDate = new Date(localEndDateTime);

        startTime = startDate.toISOString();
        endTime = endDate.toISOString();
      }

      const updateData = {
        services: this.cartItems.map(item => ({
          serviceId: item.service.id,
          optionIds: item.options.map(opt => opt.id),
          price: item.price,
          duration: item.duration,
        })),
        startTime,
        endTime,
        timeSlotId: this.selectedSlot?.configuredSlotId,
      };

      const response = await this.bookingModel.updateBooking(this.booking.id, updateData);

      if (response.status === HTTP_STATUS_CODE.OK) {
        // Si un paiement supplémentaire est requis, rediriger vers Stripe
        if (response.requiresPayment && response.checkoutUrl) {
          toastStore.info(response.message || 'Redirection vers le paiement...');
          // Rediriger vers la page de paiement Stripe
          window.location.href = response.checkoutUrl;
          return true;
        }

        // Sinon, la modification a été appliquée directement
        if (response.data) {
          runInAction(() => {
            this.booking = response.data as BookingListItem;
            this.initializeCartFromBooking();
            this.setIsEditMode(false);
          });
          toastStore.success(response.message || 'Réservation modifiée avec succès');
          return true;
        }
      }

      toastStore.error(response.message || 'Erreur lors de la modification');
      return false;
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      toastStore.error('Une erreur est survenue lors de la modification');
      return false;
    } finally {
      this.setUpdating(false);
    }
  }

  // Charger l'aperçu des disponibilités (dates disponibles)
  async loadAvailabilityOverview(): Promise<void> {
    if (!this.booking || this.cartItems.length === 0) return;

    try {
      this.loadingSlots = true;

      const services = this.cartItems.map(item => ({
        serviceId: item.service.id,
        optionIds: item.options.map(opt => opt.id)
      }));

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);

      const response = await this.availabilityModel.getProfessionalAvailabilityOverview(
        this.booking.collaboratorCompagnieId,
        undefined,
        undefined,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        undefined,
        services
      );

      if (response.success && response.data && Array.isArray(response.data)) {
        runInAction(() => {
          // Extraire les dates qui ont de la disponibilité
          this.availableDates = response.data!
            .filter(d => d.hasAvailability)
            .map(d => d.date);
        });
      }
    } catch (err) {
      console.error('Erreur lors du chargement de l\'aperçu des disponibilités:', err);
    } finally {
      runInAction(() => {
        this.loadingSlots = false;
      });
    }
  }

  // Charger les créneaux pour une date spécifique
  async loadSlotsForDate(date: Date): Promise<void> {
    if (!this.booking || this.cartItems.length === 0) return;

    try {
      this.loadingSlots = true;
      this.selectedDate = date;

      const services = this.cartItems.map(item => ({
        serviceId: item.service.id,
        optionIds: item.options.map(opt => opt.id)
      }));

      // Format date in local time without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      const response = await this.availabilityModel.getProfessionalAvailability(
        this.booking.collaboratorCompagnieId,
        undefined,
        undefined,
        formattedDate,
        undefined,
        undefined,
        services
      );

      if (response.success && response.data) {
        runInAction(() => {
          this.availableSlots = response.data || [];
        });
      }
    } catch (err) {
      console.error('Erreur lors du chargement des créneaux:', err);
    } finally {
      runInAction(() => {
        this.loadingSlots = false;
      });
    }
  }

  // Sélectionner un créneau
  selectSlot(slot: any): void {
    this.selectedSlot = slot;
  }

  // Annuler les modifications et revenir à l'état original
  cancelChanges(): void {
    this.cartItems = JSON.parse(JSON.stringify(this.originalCartItems));
    this.selectedService = null;
    this.selectedOptions = [];
    this.optionCategories = [];
    this.selectedSlot = null;
    this.selectedDate = null;
    this.availableSlots = [];
    this.availableDates = [];
    this.setIsEditMode(false);
  }

  // Formater une date
  formatDate(dateString: string | undefined): string | undefined {
    if (!dateString) return undefined;
    return formatDateInTimeZone(new Date(dateString), "EEEE d MMMM yyyy 'à' HH'h'mm");
  }

  formatShortDate(date: Date): string {
    return formatDateInTimeZone(date, "EEEE d MMMM yyyy");
  }
}

// Créer une instance singleton
const clientBookingManageViewModel = new ClientBookingManageViewModel();

// Hook pour accéder au ViewModel depuis les composants
export const useClientBookingManageViewModel = (): ClientBookingManageViewModel => {
  return clientBookingManageViewModel;
};

export { ClientBookingManageViewModel };
